import type { ReactElement } from "react"
import { createBrowserRouter, type RouteObject } from "react-router"
import { AuthCallback } from "~/features/auth/AuthCallback"
import { SilentCallback } from "~/features/auth/SilentCallback"
import { AUTH_CALLBACK_PATH, LOGOUT_CALLBACK_PATH, SILENT_CALLBACK_PATH } from "~/features/auth/userManager"
import { EventDetailPage } from "~/features/event/components/EventDetailPage"
import { EventPlayerPage } from "~/features/event/components/EventPlayerPage"
import { DigitalLibraryPage } from "~/features/library/components/DigitalLibraryPage"
import { StudyDetailPage } from "~/features/study/components/StudyDetailPage"
import { StudyPlayerPage } from "~/features/study/components/StudyPlayerPage"
import { StateMessage } from "~/shared/ui"
import { DevShell } from "./DevShell"
import { PendingFeature } from "./PendingFeature"
import { RequireAuth } from "./RequireAuth"

const pendingRoute = (path: string, label: string, ticket: string): RouteObject => ({
  path,
  element: (
    <DevShell>
      <PendingFeature label={label} ticket={ticket} />
    </DevShell>
  ),
})

const authedRoute = (path: string, element: ReactElement): RouteObject => ({
  path,
  element: (
    <DevShell>
      <RequireAuth>{element}</RequireAuth>
    </DevShell>
  ),
})

/**
 * The player draws its own full-bleed surface and must cover the shell rather
 * than sit inside its content column, so it skips `DevShell` entirely. Auth is
 * still required — the graph will not answer without it.
 */
const playerRoute = (path: string, element: ReactElement): RouteObject => ({
  path,
  element: <RequireAuth>{element}</RequireAuth>,
})

/** Standalone only. Embedded, the host owns the URL — see expose.tsx. */
export const createRouter = () =>
  createBrowserRouter([
    authedRoute("/", <DigitalLibraryPage />),
    authedRoute("/study/:id", <StudyDetailPage />),
    authedRoute("/event/:id", <EventDetailPage />),
    pendingRoute("/ebook/:id", "Ebook", "DCD-6987"),
    playerRoute("/study/:id/session/:sessionId", <StudyPlayerPage />),
    playerRoute("/event/:id/session/:sessionId", <EventPlayerPage />),
    { path: AUTH_CALLBACK_PATH, element: <AuthCallback /> },
    { path: SILENT_CALLBACK_PATH, element: <SilentCallback /> },
    {
      path: LOGOUT_CALLBACK_PATH,
      element: <StateMessage qaHook="auth-signed-out">You have been signed out.</StateMessage>,
    },
    {
      path: "*",
      element: <StateMessage qaHook="app-not-found">Page not found.</StateMessage>,
    },
  ])
